import React, { ReactNode, useState, useEffect } from 'react';
import { EditButton } from './EditButton';

interface EditableFormFieldProps {
  label: ReactNode;
  subLabel1?: string;
  subLabel2?: string;
  subLabel3?: string;
  subLabel4?: string;
  subLabel5?: string;
  subLabel6?: string; // 追加
  field: string;
  defaultValue1?: string;
  defaultValue2?: string;
  defaultValue3?: string;
  defaultValue4?: string;
  defaultValue5?: string;
  defaultValue6?: string; // 追加
  height?: number;
  onValueChange?: (values: {
    value1?: string;
    value2?: string;
    value3?: string;
    value4?: string;
    value5?: string;
    value6?: string; // 追加
  }) => void;
}

export const EditableFormField: React.FC<EditableFormFieldProps> = ({
  label,
  subLabel1,
  subLabel2,
  subLabel3,
  subLabel4,
  subLabel5,
  subLabel6, // 追加
  defaultValue1 = '',
  defaultValue2 = '',
  defaultValue3 = '',
  defaultValue4 = '',
  defaultValue5 = '',
  defaultValue6 = '', // 追加
  height = 33, // 表示エリアの高さ
  onValueChange,
}) => {
  const [values, setValues] = useState<string[]>([
    defaultValue1,
    defaultValue2,
    defaultValue3,
    defaultValue4,
    defaultValue5,
    defaultValue6, // 追加
  ]);

  useEffect(() => {
    setValues([
      defaultValue1,
      defaultValue2,
      defaultValue3,
      defaultValue4,
      defaultValue5,
      defaultValue6,
    ]);
  }, [
    defaultValue1,
    defaultValue2,
    defaultValue3,
    defaultValue4,
    defaultValue5,
    defaultValue6,
  ]);

  const [tempValue, setTempValue] = useState('');
  const [currentFieldIndex, setCurrentFieldIndex] = useState<
    number | undefined
  >(undefined);

  const subLabels = [
    subLabel1,
    subLabel2,
    subLabel3,
    subLabel4,
    subLabel5,
    subLabel6,
  ];

  // subLabel がある項目だけを取り出す
  const activeFields = subLabels
    .map((label, index) => ({
      subLabel: label,
      value: values[index],
      index,
    }))
    .filter((field) => field.subLabel !== undefined);

  const handleEditClick = (index: number) => {
    setTempValue(values[index] || '');
    setCurrentFieldIndex(index);
  };

  const handleCancel = () => {
    setCurrentFieldIndex(undefined);
  };

  const handleSave = () => {
    if (currentFieldIndex !== undefined) {
      const newValues = [...values];
      newValues[currentFieldIndex] = tempValue;
      setValues(newValues);
      if (onValueChange) {
        onValueChange({
          value1: newValues[0],
          value2: newValues[1],
          value3: newValues[2],
          value4: newValues[3],
          value5: newValues[4],
          value6: newValues[5],
        });
      }
    }
    setCurrentFieldIndex(undefined);
  };

  return (
    <div className="mb-6 border-b border-gray-400 w-full">
      <div className="flex items-start my-3 w-full">
        {/* メインラベルは幅1/6固定 */}
        <label className="font-bold w-1/6">{label}</label>
        {/* 残り 5/6 の領域にサブ項目を並べる */}
        <div className="w-5/6">
          {activeFields.map((field) => (
            <div key={field.index} className="flex items-center mb-2 w-full">
              {/* subLabel: 幅1/6 */}
              <span className="w-1/6 text-sm text-gray-600 whitespace-nowrap overflow-hidden text-ellipsis">
                {field.subLabel}
              </span>
              {/* 実際の値を表示する部分: 幅4/6 */}
              <div
                onClick={() => handleEditClick(field.index)}
                className="border border-gray-400 px-2 bg-white cursor-pointer w-4/6 
                           overflow-hidden whitespace-nowrap text-ellipsis"
                style={{
                  height: `${height}px`,
                }}
              >
                {field.value}
              </div>
              {/* 編集ボタン: 幅1/6 */}
              <div className="ml-2 w-1/6">
                <EditButton onClick={() => handleEditClick(field.index)} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* モーダル部分 */}
      {currentFieldIndex !== undefined && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded shadow-md w-full max-w-md mx-4">
            <h2 className="text-xl font-bold mb-4">{label}の編集</h2>
            <div className="flex items-start mb-4 w-full">
              {/* テキストエリアは縦スクロールできるように */}
              <textarea
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                className="border border-gray-400 p-2 rounded w-full resize-none"
                style={{
                  height: '100px', // 必要に応じて変更
                  overflow: 'auto',
                }}
              />
            </div>
            <div className="mt-6 flex flex-wrap justify-end">
              <button
                onClick={handleCancel}
                className="mr-4 mb-2 px-4 py-2 bg-gray-200 rounded"
              >
                編集をキャンセルして戻る
              </button>
              <button
                onClick={handleSave}
                className="mb-2 px-4 py-2 bg-blue-500 text-white rounded"
              >
                完了
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
